'use client';

import { Accordion, Picture, Icon, Typography } from '@workspace/ui/common';
import { cn } from '@workspace/lib';
import { handleLinkClick } from '@workspace/hooks';
import { type HeadingType } from '@workspace/lib/heading-utils';
import { getBackgroundClass } from '@workspace/lib/helper';
import { useDocumentBlock } from './useDocumentBlock';
import { SanityDocumentSection } from './document-block.types';
import { SanityImage } from '@workspace/interfaces';
import { documentBlockVariants } from '@workspace/blocks/DocumentsBlock/document-block.variants';
import type { AccordionItem } from '@workspace/interfaces';

interface DocumentBlockProps {
  block: {
    title?: HeadingType;
    image?: SanityImage;
    customBackgroundColor?: string | { hex: string };
    backgroundColor?: string;
    documentSections: SanityDocumentSection[];
    defaultExpanded?: string[];
    imagePosition?: 'left' | 'right' | 'hidden';
    isEnabled?: boolean;
  };
  index?: number;
}

export function DocumentBlock({ block }: DocumentBlockProps) {
  const {
    resolvedTitleText,
    cleanImagePosition,
    transformedAccordionItems,
    defaultExpandedIds,
    shouldRender,
    hasVisibleSections,
  } = useDocumentBlock(block);

  // Early return if component should not render
  if (!shouldRender) {
    return null;
  }

  const backgroundClass = getBackgroundClass(block.backgroundColor || block.customBackgroundColor);

  const {
    section,
    layout,
    image: imageSlot,
    accordionWrapper,
    accordionRow,
    heading,
    headingExpanded,
    arrow,
    arrowExpanded,
    divider,
    dividerExpanded,
    accordionContent,
    fileItemLabel,
    fileItemHover,
  } = documentBlockVariants();

  const renderItemRow = (item: AccordionItem, isExpanded: boolean, onToggle: () => void) => (
    <div className="transition-shadow duration-200">
      <div
        onClick={onToggle}
        className={cn(
          'flex items-center justify-between w-full focus:outline-none transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
          accordionRow(),
          isExpanded ? dividerExpanded() : divider(),
        )}
        aria-expanded={isExpanded}
        aria-controls={`accordion-content-${item.id}`}
      >
        <Typography
          variant="h4"
          className={cn(
            'transform-gpu transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] cursor-pointer motion-reduce:transition-none',
            isExpanded ? cn('scale-105', headingExpanded()) : cn('scale-100', heading()),
          )}
        >
          {item.title}
        </Typography>

        <div
          className={cn(
            'transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
            isExpanded ? 'rotate-180 scale-110' : 'rotate-0 scale-100',
          )}
        >
          <Icon
            name="ChevronDown"
            size={20}
            className={cn(
              'transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] cursor-pointer motion-reduce:transition-none',
              isExpanded ? arrowExpanded() : arrow(),
            )}
          />
        </div>
      </div>
    </div>
  );

  const renderItemContent = (
    item: AccordionItem,
    isExpanded: boolean,
    getHeight: (id: string) => number,
    contentRef: (el: HTMLDivElement | null) => void,
  ) => (
    <div
      id={`accordion-content-${item.id}`}
      className={cn(
        'overflow-y-hidden transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[height,opacity] motion-reduce:transition-none',
        isExpanded ? 'opacity-100' : 'opacity-0',
      )}
      style={{
        height: isExpanded ? getHeight(item.id) : 0,
      }}
    >
      <div className={accordionContent()} ref={contentRef}>
        {item?.items?.map((link, index) => (
          <button
            key={link.id}
            onClick={(e) => handleLinkClick(link, e)}
            className={cn(
              'block w-full text-left rounded-md cursor-pointer transform-gpu transition-[opacity,transform,color] duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
              fileItemLabel(),
              fileItemHover(),
              isExpanded ? 'translate-y-0 opacity-100' : 'translate-y-[-8px] opacity-0',
            )}
            style={{
              transitionDelay: isExpanded ? `${index * 50}ms` : '0ms',
            }}
          >
            <div className="flex items-center gap-3">
              <span className="flex-1 font-body text-base leading-6 underline decoration-solid !min-w-max">
                {link.label}
              </span>
            </div>
          </button>
        ))}

        {(!item?.items || item.items.length === 0) && (
          <div className="text-center text-black py-4">
            <p>No documents available in this section</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={cn('document-block-bg w-full', backgroundClass)}>
      <section className={cn('porta-container', section())}>
        <div className="porta-container p-0!">
          {resolvedTitleText && (
            <div className="text-center mb-12">
              <Typography variant="h2" className={cn('text-3xl font-bold text-gray-900 md:text-4xl')}>
                {resolvedTitleText}
              </Typography>
            </div>
          )}

          <div
            className={cn(
              'flex items-center justify-start md:items-start',
              layout(),
              cleanImagePosition === 'hidden' || !block.image
                ? 'flex-col'
                : cleanImagePosition === 'left'
                  ? 'flex-col md:flex-row'
                  : cleanImagePosition === 'right'
                    ? 'flex-col md:flex-row-reverse'
                    : 'flex-col md:flex-row',
            )}
          >
            {block.image?.url && cleanImagePosition !== 'hidden' && (
              <div className="image-container md:w-91.75 md:h-91.75">
                <Picture
                  image={block.image}
                  alt={block.image.bynderImage?.alt || block.image.alt || 'Resource hub image'}
                  desktopWidth={367}
                  desktopHeight={367}
                  imgClassName={cn(
                    'object-fill h-full shrink-0 hidden md:block',
                    cleanImagePosition === 'hidden' || !block.image
                      ? 'hidden'
                      : 'max-w-91.75! w-91.75 max-h-91.75! h-91.75!',
                    imageSlot(),
                  )}
                />
              </div>
            )}

            <div
              className={cn(
                cleanImagePosition === 'hidden' || !block.image
                  ? 'w-full'
                  : cn('md:flex-1 w-full items-center flex', accordionWrapper()),
              )}
            >
              {hasVisibleSections ? (
                <Accordion
                  items={transformedAccordionItems}
                  defaultExpanded={defaultExpandedIds}
                  renderItemRow={renderItemRow}
                  renderItemContent={renderItemContent}
                />
              ) : (
                <div className="text-center text-secondary-50 py-8">
                  <p>No document sections available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
